// @ts-ignore - Firebase v12 type definitions
import * as fbAuth from 'firebase/auth';
import { auth } from './config';

export type User = any;

// @ts-ignore
const googleProvider = new fbAuth.GoogleAuthProvider();

// Action code settings for email link
const actionCodeSettings = {
  url: window.location.origin,
  handleCodeInApp: true,
};

export async function signInWithGoogle(): Promise<User> {
  try {
    // @ts-ignore
    const result = await fbAuth.signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
}

export async function signOutUser(): Promise<void> {
  try {
    // @ts-ignore
    await fbAuth.signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
}

export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Firebase ID token of the signed-in user, sent as the Authorization bearer token on
 * calls to the sync functions (which verify it server-side against the shop's admins).
 */
export async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  return user.getIdToken();
}

export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  // @ts-ignore
  return fbAuth.onAuthStateChanged(auth, callback);
}

export function isAuthorized(user: User | null): boolean {
  if (!user || !user.email) {
    return false;
  }
  
  const allowedEmail = process.env.REACT_APP_ADMIN_EMAIL;
  if (!allowedEmail) {
    console.warn('REACT_APP_ADMIN_EMAIL is not set. Allowing all authenticated users.');
    return true;
  }
  
  return user.email.toLowerCase() === allowedEmail.toLowerCase();
}

export function isShopAuthorized(user: User | null, shopAdminEmails?: string[]): boolean {
  if (!user || !user.email) {
    return false;
  }
  
  if (!shopAdminEmails || !Array.isArray(shopAdminEmails) || shopAdminEmails.length === 0) {
    return false;
  }
  
  const userEmail = user.email.toLowerCase();
  return shopAdminEmails.some(email => email.toLowerCase() === userEmail);
}

/**
 * Send sign-in link to email
 */
export async function sendEmailLink(email: string, continueUrl?: string): Promise<void> {
  try {
    const settings = {
      url: continueUrl || window.location.origin + window.location.pathname,
      handleCodeInApp: true,
    };
    // @ts-ignore
    await fbAuth.sendSignInLinkToEmail(auth, email, settings);
    // Save email to localStorage so we can use it when completing sign-in
    window.localStorage.setItem('emailForSignIn', email);
  } catch (error) {
    console.error('Error sending email link:', error);
    throw error;
  }
}

/**
 * Check if current URL is a sign-in email link
 */
export function checkEmailLink(): boolean {
  // @ts-ignore
  return fbAuth.isSignInWithEmailLink(auth, window.location.href);
}

/**
 * Complete sign-in with email link
 */
export async function signInWithEmail(email: string): Promise<User> {
  try {
    // @ts-ignore
    const result = await fbAuth.signInWithEmailLink(auth, email, window.location.href);
    // Clear the email from storage
    window.localStorage.removeItem('emailForSignIn');
    return result.user;
  } catch (error) {
    console.error('Error signing in with email link:', error);
    throw error;
  }
}

/**
 * Get stored email for sign-in (from localStorage)
 */
export function getStoredEmail(): string | null {
  return window.localStorage.getItem('emailForSignIn');
}
